<template>
  <v-container fluid>
    <div id="content">

      <section class="d-flex">
        <BreadCrumb :items="$route.matched"></BreadCrumb>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click.stop="dialog = true" depressed>新しい{{ $route.name }}を登録</v-btn>
      </section>

      <section class="list">
        <h3>
          絞り込み条件
          <div class="ctrl-btn opened" @click="switchAccordion"></div>
        </h3>
        <div class="accordion_inner">
          <v-row>
            <v-col cols="6" md="3">
              <v-combobox
                ref="corporate"
                label="支払元"
                v-model="filterKeyword.corporate"
                :items="corporateLabels"
                append-icon="fas fa-list-ol"
                @click.stop="openSelected('corporate')"
                readonly outlined clearable hide-details="auto">
              </v-combobox>
            </v-col>
            <v-col cols="6" md="3">
              <v-combobox
                ref="customer"
                label="客先"
                v-model="filterKeyword.customer"
                :items="customers"
                append-icon="fas fa-list-ol"
                @click.stop="openSelected('customer')"
                readonly outlined clearable hide-details="auto">
              </v-combobox>
            </v-col>
            <v-col cols="6" md="3">
              <v-combobox
                ref="payment"
                label="支払方法"
                v-model="filterKeyword.payment"
                :items="payment"
                append-icon="fas fa-list-ol"
                @click.stop="openSelected('payment')"
                readonly outlined clearable hide-details="auto">
              </v-combobox>
            </v-col>
            <v-col cols="6" md="3">
              <v-menu
                v-model="claim_span"
                :close-on-content-click="false"
                :nudge-right="0"
                transition="scale-transition" offset-y min-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field
                    ref="claim_span"
                    label="請求日"
                    :value="claimSpanText"
                    v-on="on"
                    @click:clear="filterKeyword.claim_span = []"
                    outlined clearable hide-details="auto">
                  </v-text-field>
                </template>
                <v-date-picker 
                  v-model="filterKeyword.claim_span" 
                  :title-date-format="claimSpanFunc"
                  range></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="6" md="3">
              <v-menu
                v-model="fixed_span"
                :close-on-content-click="false"
                :nudge-right="0"
                transition="scale-transition" offset-y min-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field
                    ref="fixed_span"
                    label="支払期日"
                    :value="fixedSpanText"
                    v-on="on"
                    @click:clear="filterKeyword.fixed_span = []"
                    outlined clearable hide-details="auto">
                  </v-text-field>
                </template>
                <v-date-picker 
                  v-model="filterKeyword.fixed_span" 
                  :title-date-format="fixedSpanFunc"
                  range></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="6" md="3" class="px-5">
              <v-switch
                v-model="filterKeyword.complete"
                label="支払済を表示"
                color="success" inset></v-switch>
            </v-col>
          </v-row>
        </div>

        <template v-if="paymentDocs.length > 0">
          <h3 class="mt-5">未分類のアップロードされた請求書</h3>
          <ul class="list-inline gallery">
            <li v-for="item in paymentDocs" :key="item.id">
              <v-img 
                :src="item.src" 
                width="120"
                height="120"
                aspect-ratio="1" 
                @click="lightbox(item.src)" 
                contain></v-img>
                <div class="mask">
                  <v-icon @click.stop="paymentDocEdit(item)">fas fa-edit</v-icon>
                  <v-icon @click.stop="paymentDocRemove(item)">fas fa-trash-alt</v-icon>
                </div>
            </li>
          </ul>
        </template>

        <h3 class="mt-5">検索結果</h3>
        <v-data-table
          :headers="tableHeaders"
          :items="filterItems"
          :sort-by="['fixedDate']"
          :sort-desc="[true]"
          :loading="loading"
          @click:row="onRowClick">
          <template v-slot:item.corporate="{ item }">
            {{ item.corporate.name }}
          </template>
          <template v-slot:item.customer="{ item }">
            {{ item.customer.name }}
          </template>
          <template v-slot:item.amount="{ item }">
            &yen; {{ separate( Math.floor(item.amount) ) }}
          </template>
          <template v-slot:item.complete="{ item }">
            <template v-if="item.complete">
              <v-icon color="green darken-2">far fa-check-circle</v-icon>
            </template>
          </template>
          <template v-slot:item.fixedDate="{ item }">
            {{ getDateTime(item.fixedDate, 'YYYY-MM-DD') }}
          </template>
          <template v-slot:item.claimDate="{ item }">
            {{ getDateTime(item.claimDate, 'YYYY-MM-DD') }}
          </template>
        </v-data-table>
        <div class="total">
          <span class="label">合計</span>
          <span class="value">&yen; {{ separate(totalValue) }}</span>
        </div>
      </section>

      <!-- 編集ダイアログ -->
      <Claim
        :editedItem="editedItem"
        :corporates="corporates"
        :customers="customers"
        :show="dialog"
        @docRegistComplete="docRegistComplete"
        @close="dialog = false" />

    </div>
  </v-container>
</template>

<script>
import { API, graphqlOperation, Storage } from 'aws-amplify'
import { 
  deletePaymentDoc } from '../graphql/mutations'
import { listPayments, listCustomers, listCorporates, listPaymentDocs } from '../graphql/queries'
import { 
  onCreatePayment, onUpdatePayment, onDeletePayment,
  onCreateCustomer, onUpdateCustomer, onDeleteCustomer,
  onCreatePaymentDoc, onDeletePaymentDoc } from '../graphql/subscriptions'
import moment from 'moment'
import Claim from '@/components/Claim'

export default {
  data() {
    return {
      search: '',
      loading: true,
      tableHeaders: [
        // { text: '発注日', align: 'start', value: 'orderDate' },
        { text: '請求日', align: 'start', value: 'claimDate' },
        { text: '支払期日', align: 'start', value: 'fixedDate' },
        { text: '支払元', align: 'start', value: 'corporate' },
        { text: '客先', align: 'start', value: 'customer' },
        { text: '件名', align: 'start', value: 'title' },
        { text: '支払完了', align: 'start', value: 'complete' },
        { text: '支払金額（税込）', align: 'end', value: 'amount' },
      ],
      dialog: false,
      valid: false,
      saving: false,
      desserts: [],
      paymentDocs: [],
      customers: [],
      corporates: [],
      editedIndex: -1,
      editedItem: {
        commission: true,
        complete: false,
      },
      filterKeyword: {
        claim_span: [],
        fixed_span: [],
        complete: true,
      },
      claim_span: false,
      fixed_span: false,
    }
  },
  prop: {},
  mounted: async function() {
    let timer = setInterval(() => {
      if (this.getMyself()) {
        clearInterval(timer)
        this.init();
      }
    }, 100)

    //作成時 subscription
    await API.graphql(graphqlOperation(onCreatePayment)).subscribe({
      next: (e) => {
        const row = e.value.data.onCreatePayment
        this.desserts.push(row)
      }
    })
    //更新時 subscription
    await API.graphql(graphqlOperation(onUpdatePayment)).subscribe({
      next: (e) => {
        const row = e.value.data.onUpdatePayment
        this.desserts.find(origin => {
          if (origin.id === row.id) {
            origin = Object.assign(origin, row)
          }
        })
      }
    })
    //削除時 subscription
    await API.graphql(graphqlOperation(onDeletePayment)).subscribe({
      next: (e) => {
        const row = e.value.data.onDeletePayment
        this.desserts = this.desserts.filter(origin => origin.id !== row.id)
      }
    })
    //customer作成時 subscription
    await API.graphql(graphqlOperation(onCreateCustomer)).subscribe({
      next: (e) => {
        const data = e.value.data.onCreateCustomer
        this.customers.push({
          text: data.name,
          value: data.id,
        })
      }
    })
    //customer更新時 subscription
    await API.graphql(graphqlOperation(onUpdateCustomer)).subscribe({
      next: (e) => {
        const data = e.value.data.onUpdateCustomer
        this.customers.find(origin => {
          if (origin.value === data.id) {
            origin = Object.assign(origin, data)
          }
        })
      }
    })
    //customer削除時 subscription
    await API.graphql(graphqlOperation(onDeleteCustomer)).subscribe({
      next: (e) => {
        const data = e.value.data.onDeleteCustomer
        this.customers = this.customers.filter(origin => origin.value !== data.id)
      }
    })
    //paymentDoc 作成時 subscription
    await API.graphql(graphqlOperation(onCreatePaymentDoc)).subscribe({
      next: async (e) => {
        const data = e.value.data.onCreatePaymentDoc
        this.paymentDocs.push(Object.assign({
          src: await this.getImageURL(data.pic),
        }, data))
      }
    })
    //paymentDoc 削除時 subscription
    await API.graphql(graphqlOperation(onDeletePaymentDoc)).subscribe({
      next: (e) => {
        const data = e.value.data.onDeletePaymentDoc
        this.paymentDocs = this.paymentDocs.filter(e => e.id !== data.id)
      }
    })
  },
  beforeDestroy() {},
  created() {
    if (!this.isAllowed(this.$route.name)) this.$router.push('/')
  },
  computed: {
    filterItems() {
      const map = []
      for (const item of this.desserts) {
        // 支払元
        if (this.filterKeyword.corporate && this.filterKeyword.corporate.value != item.corporateID) continue
        item.corporate = this.corporates.find(e => e.id == item.corporateID)
        //支払先
        if (this.filterKeyword.customer && this.filterKeyword.customer.value != item.customerID) continue
        //決済方法
        if (this.filterKeyword.payment && this.filterKeyword.payment != item.payment) continue
        //請求日
        if (this.filterKeyword.claim_span.length > 0) {
          let from = this.filterKeyword.claim_span[0]
          let to = this.filterKeyword.claim_span[1] || null
          if (to) {
            if (moment(from) > moment(item.claimDate) || moment(item.claimDate) > moment(to)) continue
          } else {
            if (moment(from) > moment(item.claimDate)) continue
          }
          if (!item.claimDate) continue
        }
        //支払期日
        if (this.filterKeyword.fixed_span.length > 0) {
          let from = this.filterKeyword.fixed_span[0]
          let to = this.filterKeyword.fixed_span[1] || null
          if (to) {
            if (moment(from) > moment(item.fixedDate) || moment(item.fixedDate) > moment(to)) continue
          } else {
            if (moment(from) > moment(item.fixedDate)) continue
          }
          if (!item.fixedDate) continue
        }
        //支払済みの表示
        if (!this.filterKeyword.complete && item.complete) continue

        map.push(item)
      }
      console.log(map)
      return map
    },
    totalValue() {
      let sum = 0
      for (const item of this.filterItems) {
        sum += Math.floor(item.amount)
      }
      return sum
    },
    claimSpanText() {
      let date = this.filterKeyword.claim_span ? this.filterKeyword.claim_span : []
      date.sort(function(a,b) {
        if (a < b) return -1
        if (a > b) return 1
        return 0
      })
      return date.length > 0 ? date.join(' ～ ') : null
    },
    fixedSpanText() {
      let date = this.filterKeyword.fixed_span ? this.filterKeyword.fixed_span : []
      date.sort(function(a,b) {
        if (a < b) return -1
        if (a > b) return 1
        return 0
      })
      return date.length > 0 ? date.join(' ～ ') : null
    },
    corporateLabels() {
      const map = []

      for (const item of this.corporates) {
        map.push({
          value: item.id,
          text: item.name,
        })
      }

      return map
    }
  },
  watch: {

  },
  methods: {
    //初期化
    async init() {
      //請求書リスト query
      await this.getPayments()
      //未分類の請求書リスト query
      await this.getPaymentDocs()
      //企業情報 query
      await this.getCorporates()
      //客先 query
      await this.getCustomers()

      this.loading = false
    },
    //行選択
    async onRowClick(params) {
      try {
        this.editedIndex = this.desserts.findIndex(e => e.id === params.id)

        this.editedItem = Object.assign({
          items: [],
        }, params)

        //請求先
        this.editedItem.corporateID = this.corporateLabels.find(e => e.value == params.corporateID)
        //支払先
        this.editedItem.customerID = this.customers.find(e => e.value == params.customerID)
        this.dialog = true

      } catch (e) {
        //console.log(e)
      }
    },
    //企業
    getCorporateLabel(id) {
      const corporate = this.corporateLabels.find(e => e.value === id)
      return corporate ? corporate.text : null
    },
    //アコーディオンの開閉
    switchAccordion(e) {
      const target = e.target.parentElement.nextSibling
      target.style.display = target.style.display == 'none' ? 'block' : 'none'
      e.target.classList.toggle('opened')
    },
    //S3からイメージ取得
    async getImageURL(key) {
      const ret = await Storage.get(key)
      
      return ret
    },
    //画像のポップアップ
    lightbox(src) {
      //画像はポップアップで表示
      this.$store.dispatch('updatePreview', {
        visible: true,
        src: src,
      })
    },
    //請求書編集
    paymentDocEdit(item) {
      this.editedItem = Object.assign({
        items: [],
        pic: item.pic,
        docRegist: true,
      }, {})

      //請求先
      this.editedItem.corporateID = this.corporateLabels.find(e => e.value == item.corporateID)
      
      this.dialog = true
    },
    //請求書削除
    async paymentDocRemove(item) {
      const result = await this.$confirm('選択した写真を削除しますか？')
      if (result === true) {
        await API.graphql(graphqlOperation(deletePaymentDoc, {
          input: { id: item.id }
        }))
      }
    },
    //請求書登録された写真削除
    async docRegistComplete(pic) {
      const item = this.paymentDocs.find(e => e.pic == pic)
      await API.graphql(graphqlOperation(deletePaymentDoc, {
        input: { id: item.id }
      }))
    },
    //請求書リストの取得
    async getPayments(nextToken = null) {
      //所属企業
      const myself = this.getMyself()
      const corporateFilter = []
      for (const id of myself.corporateIDs) {
        corporateFilter.push({
          corporateID: { eq: id }
        })
      }
      const query = await API.graphql(graphqlOperation(listPayments, { 
        filter: {
          or: corporateFilter
        },
        limit: 500, 
        nextToken: nextToken 
      }))
      for (const row of query.data.listPayments.items) {
        this.desserts.push(row)
      }
      if (query.data.listPayments.nextToken) this.getPayments(query.data.listPayments.nextToken)
    },
    //未分類の請求書リストの取得
    async getPaymentDocs(nextToken = null) {
      //所属企業
      const myself = this.getMyself()
      const corporateFilter = []
      for (const id of myself.corporateIDs) {
        corporateFilter.push({
          corporateID: { eq: id }
        })
      }
      
      const query = await API.graphql(graphqlOperation(listPaymentDocs, { 
        filter: {
          or: corporateFilter
        },
        limit: 500, 
        nextToken: nextToken 
      }))
      for (const row of query.data.listPaymentDocs.items) {
        this.paymentDocs.push(Object.assign({
          src: await this.getImageURL(row.pic),
        }, row))
      }
      if (query.data.listPaymentDocs.nextToken) this.getPaymentDocs(query.data.listPaymentDocs.nextToken)
    },
    //企業の取得
    async getCorporates(nextToken = null) {
      //所属企業
      const myself = this.getMyself()
      const corporateFilter = []
      for (const id of myself.corporateIDs) {
        corporateFilter.push({
          id: { eq: id }
        })
      }
      //query
      const query = await API.graphql(graphqlOperation(listCorporates, { 
        filter: {
          or: corporateFilter
        },
        limit: 500, 
        nextToken: nextToken 
      }))
      for (const data of query.data.listCorporates.items) {
        this.corporates.push(data)
      }
      if (query.data.listCorporates.nextToken) this.getCorporates(query.data.listCorporates.nextToken)
    },
    //客先の取得
    async getCustomers(nextToken = null) {
      const query = await API.graphql(graphqlOperation(listCustomers, { limit: 500, nextToken: nextToken }))
      for (const data of query.data.listCustomers.items) {
        this.customers.push({
          value: data.id,
          text: data.name + (data.office ? '（' + data.office + '）' : ''),
          agencyID: data.agencyID || null,
        })
      }
      if (query.data.listCustomers.nextToken) this.getCustomers(query.data.listCustomers.nextToken)
    },
    //請求選択日付表示
    claimSpanFunc() {
      return this.claimSpanText || ''
    },
    //支払期日 日付表示
    fixedSpanFunc() {
      return this.fixedSpanText || ''
    }
  },
  components: {
    Claim,
  },
}
</script>

<style lang='scss' scoped>
.list {
  .total {
    text-align: right;
    font-size: 20px;
    .label {
      padding: 10px;
    }
    .value {
      display: inline-block;
      background: #EEE;
      padding: 10px;
      width: 200px;
    }
  }
}
.gallery {
  li {
    margin-right: 10px;
    position: relative;
    cursor: pointer;
    .mask {
      position: absolute;
      top: 5px;
      right: 5px;
      button {
        margin: 0 3px;
        text-shadow: 0 0 5px #FFFFFF, 0 0 5px #FFFFFF;
      }
    }
  }
}
</style>
